import * as React from "react";
import { makeStyles } from "@material-ui/core";

import { colors } from "@styles/color.styles";
import { IOrgOverview } from "../overview.types";

import { AreaSpline } from "../area-spline";
import { Column } from "../column";
import { StackedColumn } from "../stacked-column";
import { Donut } from "../donut";
import { Chart } from "../chart";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { getOrgAccountPeriodLength } from "../overview.util";

export interface ChartGridProps {
  orgOverview: IOrgOverview;
}

const useStyles = makeStyles({
  link: {
    color: colors.blueCool.standard,
    textDecoration: "none",
    transition: "color .3s",
    "&:hover": {
      color: colors.blueDusk.darker,
    },
  },
  grid: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    "& > *": {
      width: "100%",
      "@media (min-width: 1030px)": {
        width: "49%",
      },
    },
  },
});

export const ChartGrid = ({ orgOverview }: ChartGridProps) => {
  const classes = useStyles();
  const {
    userCount,
    scheduleAssistCount,
    scheduleAssistLimit,
    focusTimeHoursCreatedCount,
    conflictsResolvedCount,
    numUsersByFunction,
    numUsersByRole,
  } = orgOverview;
  const accountingPeriodLength = getOrgAccountPeriodLength(orgOverview) || 14;
  const renderCurrentUsers = () => (
    <div className="chromatic-ignore">
      <AreaSpline
        series={{ name: "Users", data: userCount, threshold: null }}
        tooltip={{ valueSuffix: " users" }}
      />
    </div>
  );

  const renderScheduleAssists = () => {
    const series = {
      name: "Schedule assists",
      data: scheduleAssistCount,
    };

    return (
      <div className="chromatic-ignore">
        {scheduleAssistLimit ? (
          <StackedColumn
            series={series}
            limit={scheduleAssistLimit}
            accountingPeriodLength={accountingPeriodLength}
          />
        ) : (
          <Column series={series} accountingPeriodLength={accountingPeriodLength} />
        )}
      </div>
    );
  };

  const renderFocusTimeHoursCreated = () => (
    <div className="chromatic-ignore">
      <Column
        series={{
          name: "Focus Time hours created",
          data: focusTimeHoursCreatedCount,
          threshold: null,
        }}
        accountingPeriodLength={accountingPeriodLength}
        tooltip={{ valueSuffix: " hrs" }}
      />
    </div>
  );

  const renderConflictsResolved = () => (
    <div className="chromatic-ignore">
      <Column
        series={{
          name: "Conflicts resolved",
          data: conflictsResolvedCount,
          threshold: null,
        }}
        accountingPeriodLength={accountingPeriodLength}
      />
    </div>
  );

  return (
    <div className={classes.grid}>
      <Chart
        title="Users"
        subtitle="People in your organization who are using Clockwise."
        chart={renderCurrentUsers()}
      />
      <Chart
        title="Schedule assists"
        subtitle={
          <p>
            Each time Clockwise schedules or reschedules an event for an attendee.&nbsp;
            <a
              className={classes.link}
              href={articleUrls.scheduleAssists}
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            </a>
            .
          </p>
        }
        chart={renderScheduleAssists()}
      />
      <Chart
        title="Focus Time hours created"
        subtitle="Uninterrupted work time blocks of two or more hours created by Clockwise."
        chart={renderFocusTimeHoursCreated()}
      />
      <Chart
        title="Conflicts resolved"
        subtitle="Each time Clockwise resolves a meeting conflict for an attendee."
        chart={renderConflictsResolved()}
      />
      <Chart
        title="Users by function"
        subtitle="Self-reported function when users sign up for Clockwise."
        chart={<Donut data={numUsersByFunction} />}
      />
      <Chart
        title="Users by role"
        subtitle="Self-reported role when users sign up for Clockwise."
        chart={<Donut data={numUsersByRole} />}
      />
    </div>
  );
};
